import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from './store';
import { observeIdToken } from '../lib/firebase';
import { ADMIN_EMAIL } from '../lib/env';
import LoadingSpinner from '../components/LoadingSpinner';

interface AdminGuardProps {
  children: React.ReactNode;
}

const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const navigate = useNavigate();
  const { isAuthenticated, initialized } = useSelector((state: RootState) => state.auth);
  const [hasAdminClaim, setHasAdminClaim] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      // ProtectedRoute handles redirect when auth is initialized; avoid loops here.
      return;
    }

    let isMounted = true;

    const checkAdminStatus = async () => {
      const unsubscribe = observeIdToken(async firebaseUser => {
        if (!isMounted) return;

        if (firebaseUser) {
          // Force token refresh to get latest claims
          await firebaseUser.getIdToken(true);
          const idTokenResult = await firebaseUser.getIdTokenResult(true);

          const emailAdmin = (firebaseUser.email || '').toLowerCase() === ADMIN_EMAIL.toLowerCase();
          const hasAdminClaimFromToken = idTokenResult.claims.isAdmin === true;

          const isAdmin = emailAdmin || hasAdminClaimFromToken;

          // If email matches but claim is missing, retry up to 3 times with delay
          if (emailAdmin && !hasAdminClaimFromToken && retryCount < 3) {
            console.log(
              `Admin email detected but claim missing, retrying... (${retryCount + 1}/3)`
            );
            setTimeout(() => {
              if (isMounted) {
                setRetryCount(prev => prev + 1);
              }
            }, 2000);
            return;
          }

          setHasAdminClaim(isAdmin);
        } else {
          setHasAdminClaim(false);
        }
        setChecking(false);
      });

      return unsubscribe;
    };

    const cleanup = checkAdminStatus();

    return () => {
      isMounted = false;
      cleanup.then(unsub => unsub?.());
    };
  }, [isAuthenticated, navigate, retryCount]);

  // Still initializing or checking admin status
  if (!initialized || checking) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" text="Checking admin access..." />
      </div>
    );
  }

  // Not authenticated or not admin
  if (!hasAdminClaim) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="mb-6">
            <svg
              className="mx-auto h-16 w-16 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">
            You don't have permission to access the admin console. This area is restricted to
            authorized administrators only.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Has admin access
  return <>{children}</>;
};

export default AdminGuard;
